import { Box, Stack, TextField, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { HttpStatusCode } from 'axios';
import { useFormik } from 'formik';
import { join } from 'lodash';
import { Search } from 'react-feather';
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import FormikForm from 'src/components/common/FormikForm';
import FormAutoCompleteByCountry from 'src/components/Form/FormAutoCompleteByCountry';
import ModalComponent from 'src/components/Modal/ModalComponent';
import PublicService from 'src/services/api/public';
import { COUNTRIES } from 'src/utils/constants/countries';
import { sortObjectsArrayByKey } from 'src/utils/helpers/arrayHelpers';
import { useSingleHSCodePredictionStore } from 'src/zustand/useAILabelingStore';
import { IFrameType, usePopupRedirectStore } from 'src/zustand/usePopupRedirectStore';

const FormAdjustment = () => {
  const {
    visiblePopupMatchAgain,
    dispatch,
    productDescription,
    materials,
    structure,
    usage,
    productionProcess,
    country,
    technicalSpecifications,
  } = useSingleHSCodePredictionStore();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: PublicService.getSentencePrediction,
  });
  const { dispatch: dispatchPopup } = usePopupRedirectStore();

  const formik = useFormik({
    initialValues: {},
    async onSubmit(values, formikHelpers) {
      if (!country) return;
      const sentence = join([materials, structure, usage, productDescription, technicalSpecifications], ' ');
      const payload = {
        sentence: sentence,
        country_code: country.code,
      };
      const res = await mutateAsync(payload);
      if (res.status_code === HttpStatusCode.TooManyRequests) {
        dispatchPopup({ visible: true, iframe: IFrameType.AiHsCodeLabeling });
      }
      dispatch(res, 'singlePredictions');
      dispatch(false, 'visiblePopupMatchAgain');
    },
  });

  return (
    <ModalComponent
      styleBox={{ width: 600 }}
      open={visiblePopupMatchAgain}
      handleClose={() => dispatch(false, 'visiblePopupMatchAgain')}
    >
      <FormikForm formik={formik}>
        <Stack spacing={3}>
          <Box textAlign={'center'}>
            <Typography variant="h5">Feedback Loop</Typography>
            <Typography variant="body2" color={'text.secondary'}>
              Please enter your product detail below. <br /> The more fields you complete, the more targeted your
              results will be.
            </Typography>
          </Box>

          <Box mt={1} />

          <FormAutoCompleteByCountry
            id={'product_country'}
            countryCodeField={'code'}
            getOptionKeyFn={(option) => `${option['code']}-${option['phone']}`}
            labelField={'label'}
            required
            value={country}
            placeholder={"Product's Country"}
            countryCode={country?.code}
            onChange={(value: any) => {
              dispatch(value, 'country');
            }}
            options={sortObjectsArrayByKey(COUNTRIES, 'label')}
          />

          <TextField
            defaultValue={productDescription}
            onChange={(e) => dispatch(e.target.value, 'productDescription')}
            required
            multiline
            rows={6}
            size="small"
            label="Please Describe In Detail"
          />
          <TextField
            defaultValue={materials}
            onChange={(e) => dispatch(e.target.value, 'materials')}
            size="small"
            label="Materials"
          />
          <TextField
            defaultValue={structure}
            onChange={(e) => dispatch(e.target.value, 'structure')}
            size="small"
            label="Structure"
          />
          <TextField
            defaultValue={usage}
            onChange={(e) => dispatch(e.target.value, 'usage')}
            size="small"
            label="Usage"
          />
          <TextField
            defaultValue={productionProcess}
            onChange={(e) => dispatch(e.target.value, 'productionProcess')}
            size="small"
            label="Production process"
          />
          <TextField
            defaultValue={technicalSpecifications}
            onChange={(e) => dispatch(e.target.value, 'technicalSpecifications')}
            size="small"
            label="Technical specifications"
          />

          <ButtonWithLoading
            isLoading={isPending}
            type="submit"
            variant="contained"
            size="large"
            isRounded
            endIcon={<Search size={18} />}
          >
            Match Again
          </ButtonWithLoading>
        </Stack>
      </FormikForm>
    </ModalComponent>
  );
};

export default FormAdjustment;
